/* eslint-disable no-param-reassign */
import {createAsyncThunk, createSlice} from '@reduxjs/toolkit'
import {http} from 'hiya-common/src/utils/axios'
import {useSelector} from 'react-redux'

import {HomepageGuideMock} from './mockMd/heart_voyage_homepage'
import {
  DailyInfoGuideMock,
  resolveDailyInfoMock,
} from './mockMd/heart_voyage_daily_info'
import {HeartVoyageJumpRoomMock} from './mockMd/heart_voyage_jump_room'
import {HeartVoyageStageClaimMock} from './mockMd/heart_voyage_stage_claim'
import {
  HeartVoyageStageInfoGuideMock,
  resolveStageInfoMock,
} from './mockMd/heart_voyage_stage_info'
import {HeartVoyageStageRemindMock} from './mockMd/heart_voyage_stage_remind'
import {
  HeartVoyageInviteHandleDeclineMock,
  HeartVoyageInviteHandleMock,
  HeartVoyageInviteListEmptyMock,
  HeartVoyageInviteListMock,
  HeartVoyageInviteListSearchMock,
  HeartVoyageInvitePendingEmptyMock,
  HeartVoyageInvitePendingMock,
  HeartVoyageInvitePendingSearchMock,
  HeartVoyageInviteSendMock,
  HeartVoyagePeakRankFinalsMock,
  HeartVoyagePeakRankMock,
  HeartVoyageRelationListMock,
  HeartVoyageRelationListWithLimitMock,
  HeartVoyageUnbindMock,
} from './mockMd'
import {SERVICE_MOCK_ENABLED, heart_voyage_homepage} from './service'
import type {
  HeartVoyageDailyInfoData,
  HeartVoyageHomepageData,
  HeartVoyagePeakRankData,
  HeartVoyagePendingInviteItem,
  HeartVoyageStageClaimData,
  HeartVoyageStageInfoData,
  HeartVoyageStageRemindData,
  InviteHandleData,
  InviteListData,
  InvitePendingData,
  InviteSendData,
  JumpRoomData,
  RelationListData,
  UnbindData,
} from './types'

export type DualCompetitionState = HeartVoyageHomepageData & {
  homepageLoaded: boolean
  guideDisplayActive: boolean
  stage_info: HeartVoyageStageInfoData | null
  stageInfoLoaded: boolean
  daily_info: HeartVoyageDailyInfoData | null
  dailyInfoLoaded: boolean
  relation_list: RelationListData['list'] | null
  relationListLoaded: boolean
  new_pending_invites: HeartVoyagePendingInviteItem[]
}

type PairKeyArg = {pair_key?: string} | void
type HomepageArg = {pair_key?: string; is_pop?: boolean} | void

const pairKeyBody = (arg: PairKeyArg) => {
  const pair_key = arg && typeof arg === 'object' ? arg.pair_key : undefined
  return pair_key ? {pair_key} : {}
}

const homepageBody = (arg: HomepageArg) => {
  const pair_key = arg && typeof arg === 'object' ? arg.pair_key : undefined
  const is_pop = arg && typeof arg === 'object' ? arg.is_pop : undefined
  return {
    ...(pair_key ? {pair_key} : {}),
    ...(is_pop === true ? {is_pop: true} : {}),
  }
}

export const get_homepage = createAsyncThunk<
  HeartVoyageHomepageData,
  HomepageArg
>('DualCompetition/get_homepage', async (arg, thunkAPI: any) => {
  const body = homepageBody(arg)
  if (SERVICE_MOCK_ENABLED) {
    return heart_voyage_homepage(body)
  }

  const resp = await http.post('/live/activity/heart_voyage_homepage', body, {
    store: thunkAPI,
  })
  return resp.data?.data as HeartVoyageHomepageData
})

export const get_stage_info = createAsyncThunk<
  HeartVoyageStageInfoData,
  PairKeyArg
>('DualCompetition/get_stage_info', async (arg, thunkAPI: any) => {
  if (SERVICE_MOCK_ENABLED) {
    return resolveStageInfoMock()
  }

  const resp = await http.post(
    '/live/activity/heart_voyage_stage_info',
    pairKeyBody(arg),
    {
      store: thunkAPI,
    }
  )
  return resp.data?.data as HeartVoyageStageInfoData
})

export const get_daily_info = createAsyncThunk<
  HeartVoyageDailyInfoData,
  PairKeyArg
>('DualCompetition/get_daily_info', async (arg, thunkAPI: any) => {
  const pair_key = arg && typeof arg === 'object' ? arg.pair_key : undefined
  if (SERVICE_MOCK_ENABLED) {
    return resolveDailyInfoMock(pair_key)
  }

  const resp = await http.post(
    '/live/activity/heart_voyage_daily_info',
    pairKeyBody(arg),
    {
      store: thunkAPI,
    }
  )
  return resp.data?.data as HeartVoyageDailyInfoData
})

export const post_stage_claim = createAsyncThunk<
  HeartVoyageStageClaimData,
  PairKeyArg
>('DualCompetition/post_stage_claim', async (arg, thunkAPI: any) => {
  if (SERVICE_MOCK_ENABLED) {
    return HeartVoyageStageClaimMock
  }

  const resp = await http.post(
    '/live/activity/heart_voyage_stage_claim',
    pairKeyBody(arg),
    {
      store: thunkAPI,
    }
  )
  return resp.data?.data as HeartVoyageStageClaimData
})

export const post_stage_remind = createAsyncThunk<
  HeartVoyageStageRemindData,
  PairKeyArg
>('DualCompetition/post_stage_remind', async (arg, thunkAPI: any) => {
  if (SERVICE_MOCK_ENABLED) {
    return HeartVoyageStageRemindMock
  }

  const resp = await http.post(
    '/live/activity/heart_voyage_stage_remind',
    pairKeyBody(arg),
    {
      store: thunkAPI,
    }
  )
  return resp.data?.data as HeartVoyageStageRemindData
})

export const post_jump_room = createAsyncThunk<JumpRoomData, {to_mid: number}>(
  'DualCompetition/post_jump_room',
  async (arg, thunkAPI: any) => {
    if (SERVICE_MOCK_ENABLED) {
      return HeartVoyageJumpRoomMock
    }

    const resp = await http.post(
      '/live/activity/heart_voyage_jump_room',
      {to_mid: arg.to_mid},
      {
        store: thunkAPI,
      }
    )
    return resp.data?.data as JumpRoomData
  }
)

export const get_peak_rank = createAsyncThunk<
  HeartVoyagePeakRankData,
  {segment: number; group_id?: number}
>('DualCompetition/get_peak_rank', async (arg, thunkAPI: any) => {
  if (SERVICE_MOCK_ENABLED) {
    if (arg.segment === 2) {
      return HeartVoyagePeakRankFinalsMock
    }
    return HeartVoyagePeakRankMock
  }

  const resp = await http.post(
    '/live/activity/heart_voyage_peak_rank',
    {
      segment: arg.segment,
      group_id: arg.group_id ?? 0,
    },
    {
      store: thunkAPI,
    }
  )
  return resp.data?.data as HeartVoyagePeakRankData
})

export const get_invite_list = createAsyncThunk<
  InviteListData,
  {search_mid?: number} | void
>('DualCompetition/get_invite_list', async (arg, thunkAPI: any) => {
  const search_mid = arg && typeof arg === 'object' ? arg.search_mid ?? 0 : 0
  if (SERVICE_MOCK_ENABLED) {
    if (!search_mid) {
      return HeartVoyageInviteListMock
    }
    if (search_mid === 123) {
      return HeartVoyageInviteListSearchMock
    }
    return HeartVoyageInviteListEmptyMock
  }

  const resp = await http.post(
    '/live/activity/heart_voyage_invite_list',
    {search_mid},
    {
      store: thunkAPI,
    }
  )
  return resp.data?.data as InviteListData
})

export const get_invite_pending = createAsyncThunk<
  InvitePendingData,
  {search_mid?: number} | void
>('DualCompetition/get_invite_pending', async (arg, thunkAPI: any) => {
  const search_mid = arg && typeof arg === 'object' ? arg.search_mid ?? 0 : 0
  if (SERVICE_MOCK_ENABLED) {
    if (!search_mid) {
      return HeartVoyageInvitePendingMock
    }
    if (search_mid === 123) {
      return HeartVoyageInvitePendingSearchMock
    }
    return HeartVoyageInvitePendingEmptyMock
  }

  const resp = await http.post(
    '/live/activity/heart_voyage_invite_pending',
    {
      search_mid,
    },
    {
      store: thunkAPI,
    }
  )
  return resp.data?.data as InvitePendingData
})

export const post_invite_send = createAsyncThunk<
  InviteSendData,
  {to_mid: number; rel_type: number}
>('DualCompetition/post_invite_send', async (arg, thunkAPI: any) => {
  if (SERVICE_MOCK_ENABLED) {
    return HeartVoyageInviteSendMock
  }

  const resp = await http.post(
    '/live/activity/heart_voyage_invite_send',
    {
      to_mid: arg.to_mid,
      rel_type: arg.rel_type,
    },
    {
      store: thunkAPI,
    }
  )
  return resp.data?.data as InviteSendData
})

export const post_invite_handle = createAsyncThunk<
  InviteHandleData,
  {invite_id: string; action: number}
>('DualCompetition/post_invite_handle', async (arg, thunkAPI: any) => {
  if (SERVICE_MOCK_ENABLED) {
    if (arg.action === 2) {
      return HeartVoyageInviteHandleDeclineMock
    }
    return HeartVoyageInviteHandleMock
  }

  const resp = await http.post(
    '/live/activity/heart_voyage_invite_handle',
    {
      invite_id: arg.invite_id,
      action: arg.action,
    },
    {
      store: thunkAPI,
    }
  )
  return resp.data?.data as InviteHandleData
})

export const get_relation_list = createAsyncThunk<RelationListData, PairKeyArg>(
  'DualCompetition/get_relation_list',
  async (arg, thunkAPI: any) => {
    const pair_key = arg && typeof arg === 'object' ? arg.pair_key : undefined
    if (SERVICE_MOCK_ENABLED) {
      if (pair_key) {
        return HeartVoyageRelationListWithLimitMock
      }
      return HeartVoyageRelationListMock
    }

    const resp = await http.post(
      '/live/activity/heart_voyage_relation_list',
      {},
      {
        store: thunkAPI,
      }
    )
    return resp.data?.data as RelationListData
  }
)

export const post_unbind = createAsyncThunk<UnbindData, {pair_key: string}>(
  'DualCompetition/post_unbind',
  async (arg, thunkAPI: any) => {
    if (SERVICE_MOCK_ENABLED) {
      return HeartVoyageUnbindMock
    }

    const resp = await http.post(
      '/live/activity/heart_voyage_unbind',
      {pair_key: arg.pair_key},
      {
        store: thunkAPI,
      }
    )
    return resp.data?.data as UnbindData
  }
)

const initialState: DualCompetitionState = {
  homepageLoaded: false,
  guideDisplayActive: false,
  stage_info: null,
  stageInfoLoaded: false,
  daily_info: null,
  dailyInfoLoaded: false,
  relation_list: null,
  relationListLoaded: false,
  err_msg: '',
  activity_id: 0,
  stage: 0,
  schedule_segment: 1,
  need_newbie_guide: false,
  new_pending_invites: [],
  countdown: 0,
  normal_gift_rate: 1,
  special_gift_rate: 1,
  special_affinity_rate: 0,
  wheel_game_url: '',
  gold_miner_game_url: '',
  jump_blind_box_gift_id: 0,
  audition_start_time: '',
  audition_end_time: '',
  final_start_time: '',
  final_end_time: '',
  max_level_add: 0,
  form_stages: [],
  header_pair: null,
  pair_key: '',
  show_switch_relation: false,
  self: null,
  current_relation: null,
  ship_rewards: [],
  daily_rewards: [],
  peak_audition_rewards: [],
  peak_final_rewards: [],
  bind_rewards: [],
  final_groups: [],
  current_final_group_id: 0,
}

export const DualCompetitionSlice = createSlice({
  name: 'DualCompetition',
  initialState,
  reducers: {
    enterGuideDisplay(state) {
      if (state.guideDisplayActive) {
        return
      }
      const {pair_key} = state
      const needNewbieGuide = state.need_newbie_guide
      const newPendingInvites = state.new_pending_invites
      Object.assign(state, HomepageGuideMock)
      state.pair_key = pair_key
      state.need_newbie_guide = needNewbieGuide
      state.new_pending_invites = newPendingInvites
      state.daily_info = DailyInfoGuideMock
      state.dailyInfoLoaded = true
      state.stage_info = HeartVoyageStageInfoGuideMock
      state.stageInfoLoaded = true
      state.guideDisplayActive = true
    },
    exitGuideDisplay(state) {
      state.guideDisplayActive = false
      state.daily_info = null
      state.dailyInfoLoaded = false
      state.stage_info = null
      state.stageInfoLoaded = false
    },
    setStageActionStatus(state, action: {payload: number}) {
      if (!state.stage_info) {
        return
      }
      state.stage_info.action_status = action.payload
    },
  },
  extraReducers: (builder) => {
    builder.addCase(get_homepage.fulfilled, (state, action) => {
      if (state.guideDisplayActive) {
        return
      }
      const {new_pending_invites, ...homepage} = action.payload
      Object.assign(state, {
        ...homepage,
        homepageLoaded: true,
      })
      if (new_pending_invites != null) {
        state.new_pending_invites = new_pending_invites
      }
    })
    builder.addCase(get_stage_info.fulfilled, (state, action) => {
      if (state.guideDisplayActive) {
        return
      }
      state.stage_info = action.payload
      state.stageInfoLoaded = true
    })
    builder.addCase(get_daily_info.fulfilled, (state, action) => {
      if (state.guideDisplayActive) {
        return
      }
      state.daily_info = action.payload
      state.dailyInfoLoaded = true
    })
    builder.addCase(get_relation_list.fulfilled, (state, action) => {
      state.relation_list = action.payload.list ?? null
      state.relationListLoaded = true
    })
  },
})

export const {enterGuideDisplay, exitGuideDisplay, setStageActionStatus} =
  DualCompetitionSlice.actions

export default DualCompetitionSlice.reducer

export const useReducerState = () => {
  return useSelector(
    (state: {DualCompetition?: DualCompetitionState}) =>
      state.DualCompetition ?? initialState
  )
}
